import React, { useEffect, useRef, useState } from 'react'
import IconButton from '@material-ui/core/IconButton'
import Close from '../../../../Spa.Core/components/_svg/Close'
import { InformationBannerProps } from './types'
import './informationBanner.scss'

type Props = {
  props: InformationBannerProps | string
}

const informationBannerClosedStorageKey = 'nordlock.informationBanner.closed'
const informationBannerClosedStorageValue = 'true'

const getInformationBannerProps = (
  props: InformationBannerProps | string
): InformationBannerProps => {
  const parsedProps = (() => {
    if (typeof props !== 'string') {
      return props
    }

    try {
      return JSON.parse(props)
    } catch {
      return {}
    }
  })()
  const normalizedProps = parsedProps as InformationBannerProps & {
    Message?: string | null
    ReadMoreLink?: string | null
    ReadMoreText?: string | null
  }

  return {
    message: normalizedProps.message || normalizedProps.Message || null,
    readMoreLink:
      normalizedProps.readMoreLink || normalizedProps.ReadMoreLink || null,
    readMoreText:
      normalizedProps.readMoreText || normalizedProps.ReadMoreText || null
  }
}

const setElementsTopOffset = (selector: string, topOffset: string) => {
  document.querySelectorAll<HTMLElement>(selector).forEach((element) => {
    element.style.top = topOffset
  })
}

const resetElementsTopOffset = (selector: string) => {
  document.querySelectorAll<HTMLElement>(selector).forEach((element) => {
    element.style.removeProperty('top')
  })
}

const hasClosedInformationBanner = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return false
  }

  try {
    return (
      window.sessionStorage.getItem(informationBannerClosedStorageKey) ===
      informationBannerClosedStorageValue
    )
  } catch {
    return false
  }
}

const setInformationBannerClosed = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return
  }

  try {
    window.sessionStorage.setItem(
      informationBannerClosedStorageKey,
      informationBannerClosedStorageValue
    )
  } catch {
    return
  }
}

const InformationBanner = ({ props }: Props) => {
  const { message, readMoreLink, readMoreText } =
    getInformationBannerProps(props)
  const [isVisible, setIsVisible] = useState(
    () => !hasClosedInformationBanner()
  )
  const bannerRef = useRef<HTMLElement>(null)

  const handleClose = () => {
    setInformationBannerClosed()
    setIsVisible(false)
  }

  useEffect(() => {
    const previousBodyPaddingTop = document.body.style.paddingTop

    const resetBannerOffset = () => {
      document.body.classList.remove('has-information-banner')
      document.body.style.paddingTop = previousBodyPaddingTop
      document.documentElement.style.removeProperty(
        '--information-banner-height'
      )
      resetElementsTopOffset('.header, .mobileHeader, .color-stripe')
      resetElementsTopOffset('.backdrop')
    }

    if (!isVisible || !message || !bannerRef.current) {
      resetBannerOffset()
      return undefined
    }

    const updateBannerOffset = () => {
      const bannerHeight = bannerRef.current?.offsetHeight || 0
      const contentOffset = `${bannerHeight}px`

      document.body.classList.add('has-information-banner')
      document.body.style.paddingTop = contentOffset
      document.documentElement.style.setProperty(
        '--information-banner-height',
        `${bannerHeight}px`
      )
      setElementsTopOffset(
        '.header, .mobileHeader, .color-stripe',
        contentOffset
      )
      setElementsTopOffset('.backdrop', `${bannerHeight + 69}px`)
    }

    updateBannerOffset()
    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(updateBannerOffset)
        : null

    if (resizeObserver && bannerRef.current) {
      resizeObserver.observe(bannerRef.current)
    }

    window.addEventListener('resize', updateBannerOffset)

    return () => {
      resizeObserver?.disconnect()
      window.removeEventListener('resize', updateBannerOffset)
      resetBannerOffset()
    }
  }, [isVisible, message])

  if (!isVisible || !message) {
    return null
  }

  return (
    <aside className="information-banner" ref={bannerRef}>
      <div className="information-banner__inner">
        <div
          className="information-banner__message"
          dangerouslySetInnerHTML={{ __html: message }}
        />
        {readMoreLink && readMoreText && (
          <a className="information-banner__link" href={readMoreLink}>
            {readMoreText}
          </a>
        )}
        <IconButton
          className="information-banner__close"
          onClick={handleClose}
          aria-label="Close information banner"
        >
          <Close />
        </IconButton>
      </div>
    </aside>
  )
}

export default InformationBanner
